import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";

const emptyForm = {
  name: "",
  startDate: "",
  endDate: "",
  payDate: "",
  fiscalYear: new Date().getFullYear(),
  countryCode: "EG",
};

const statusColors = {
  open: "success",
  processing: "info",
  closed: "warning",
  locked: "default",
};

// New periods always start "open" (backend-controlled). Editing (PUT) is
// only permitted by the backend while status is "open" - once it moves on,
// this dialog switches to read-only. Status changes themselves (process /
// close / lock) live on the Period Dashboard, not here.
const AddPayrollPeriodDialog = ({ open, onClose, onSubmit, period, editMode, handleDelete }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm);

  const isLocked = editMode && period?.status !== "open";

  useEffect(() => {
    if (open) {
      setForm(period ? { ...period } : emptyForm);
    }
  }, [open, period]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const canSubmit =
    !isLocked &&
    !!form.name?.trim() &&
    !!form.startDate &&
    !!form.endDate &&
    !!form.payDate &&
    !!form.fiscalYear &&
    !!form.countryCode?.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1.5 }}>
        {editMode
          ? t("admin.payroll.periodsAndRuns.list.editTitle")
          : t("admin.payroll.periodsAndRuns.list.addTitle")}
        {editMode && period?.status && (
          <Chip
            size="small"
            label={t(`admin.payroll.periodsAndRuns.statuses.${period.status}`)}
            color={statusColors[period.status] || "default"}
          />
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isLocked && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={t("admin.payroll.periodsAndRuns.list.readOnlyHelper")}
              sx={{ alignSelf: "flex-start" }}
            />
          )}
          <TextField
            label={t("admin.payroll.periodsAndRuns.list.fields.name")}
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            required
            disabled={isLocked}
          />
          <TextField
            label={t("admin.payroll.periodsAndRuns.list.fields.startDate")}
            type="date"
            value={form.startDate}
            onChange={handleChange("startDate")}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
            disabled={isLocked}
          />
          <TextField
            label={t("admin.payroll.periodsAndRuns.list.fields.endDate")}
            type="date"
            value={form.endDate}
            onChange={handleChange("endDate")}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
            disabled={isLocked}
          />
          <TextField
            label={t("admin.payroll.periodsAndRuns.list.fields.payDate")}
            type="date"
            value={form.payDate}
            onChange={handleChange("payDate")}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
            disabled={isLocked}
          />
          <TextField
            label={t("admin.payroll.periodsAndRuns.list.fields.fiscalYear")}
            type="number"
            value={form.fiscalYear}
            onChange={handleChange("fiscalYear")}
            fullWidth
            required
            disabled={isLocked}
          />
          <TextField
            label={t("admin.payroll.periodsAndRuns.list.fields.countryCode")}
            value={form.countryCode}
            onChange={handleChange("countryCode")}
            fullWidth
            required
            disabled={isLocked}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={onClose} color="secondary">
          {t("common.cancel")}
        </MDButton>
        {editMode && period?.status === "open" && (
          <MDButton onClick={handleDelete} color="error">
            {t("common.delete")}
          </MDButton>
        )}
        {!isLocked && (
          <MDButton onClick={() => onSubmit(form)} color="primary" disabled={!canSubmit}>
            {editMode ? t("common.update") : t("common.add")}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

AddPayrollPeriodDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  period: PropTypes.object,
  editMode: PropTypes.bool,
  handleDelete: PropTypes.func,
};

AddPayrollPeriodDialog.defaultProps = {
  period: null,
  editMode: false,
  handleDelete: () => {},
};

export default AddPayrollPeriodDialog;
